import { merge, Observable, of, Subject, timer } from 'rxjs';
import { catchError, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';

import { Injectable, OnDestroy } from '@angular/core';

import { FsPresenceDevice, SnLocation } from '@lib/api/entities';
import { FsAxonService, PresenceService } from '@lib/axon';

import { FloorAlertState, FloorAlertStateService } from './floor-alert-state.service';
import { LocationGeometryUtils } from './location-geometry.utils';

/**
 * Service responsible for monitoring floor-level device alerts at a location.
 *
 * This service:
 * - Polls presence device data every 10 seconds
 * - Calculates venue bounding box for device queries
 * - Aggregates device data into floor alert states
 * - Provides reactive observable stream of alert states
 *
 * Dependencies:
 * - PresenceService: For fetching device data
 * - FsAxonService: For agency ID
 * - FloorAlertStateService: For aggregating alerts
 *
 * Usage:
 * ```typescript
 * // Subscribe to monitor a location
 * this.floorAlertStates$ = this.monitoringService.monitorLocation(location);
 *
 * // Stop monitoring
 * this.monitoringService.stopMonitoring();
 * ```
 */
@Injectable()
export class FloorAlertMonitoringService implements OnDestroy {
  /**
   * Zoom level used for floor alert queries.
   * Building/venue scale zoom level to ensure consistent floor-level data.
   */
  private readonly FLOOR_ALERT_QUERY_ZOOM = 15;

  /**
   * Polling interval in milliseconds.
   */
  private readonly POLLING_INTERVAL_MS = 10000;

  private readonly _destroy$ = new Subject<void>();
  private readonly _cancelMonitoring$ = new Subject<void>();

  constructor(
    private readonly _presenceService: PresenceService,
    private readonly _axonService: FsAxonService,
    private readonly _floorAlertStateService: FloorAlertStateService,
  ) {}

  /**
   * Starts monitoring floor alerts for a given location.
   *
   * Returns an observable that:
   * - Emits immediately (timer starts at 0)
   * - Polls every 10 seconds
   * - Automatically aggregates device data into floor states
   * - Handles errors gracefully (returns empty map)
   * - Completes when stopMonitoring() is called or service is destroyed
   *
   * @param location - Location to monitor (must have floors)
   * @returns Observable of floor alert states (Map<floorId, FloorAlertState>)
   */
  public monitorLocation(location: SnLocation): Observable<Map<string, FloorAlertState>> {
    // Validate prerequisites
    if (!location?.floors?.length) {
      return of(new Map());
    }

    if (!this._axonService.agencyId) {
      console.warn('[FloorAlertMonitoring] Cannot monitor: missing agency ID');
      return of(new Map());
    }

    // Calculate venue bounding box for device queries
    const venueBbox = LocationGeometryUtils.calculateVenueBbox(location);

    // Set up polling observable
    return timer(0, this.POLLING_INTERVAL_MS).pipe(
      switchMap(() =>
        this._presenceService.getPresenceDevices(
          this._axonService.agencyId,
          'axon',
          [venueBbox],
          this.FLOOR_ALERT_QUERY_ZOOM,
        ),
      ),
      map((devices: FsPresenceDevice[]) =>
        this._floorAlertStateService.aggregateFloorAlerts(devices, location.floors),
      ),
      catchError((err) => {
        console.error('[FloorAlertMonitoring] Failed to fetch presence devices:', err);
        return of(new Map<string, FloorAlertState>());
      }),
      takeUntil(merge(this._destroy$, this._cancelMonitoring$)),
      shareReplay(1), // Cache latest value for late subscribers
    );
  }

  /**
   * Stops the current monitoring session.
   * Safe to call multiple times.
   */
  public stopMonitoring(): void {
    this._cancelMonitoring$.next();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._cancelMonitoring$.complete();
  }
}

